import React from 'react'
import { Link } from 'react-router-dom'
import { FaCheck, FaTimes } from 'react-icons/fa'
import dayjs from 'dayjs'

const dateFormat = 'MM/DD/YYYY hh:mm:ss A'

const dateFilterFn = (row, columnId, filterValue) => {
    return row.getValue(columnId).includes(filterValue)
}

const createDateField = (col) => ({
    header: () => <div>{col}</div>,
    id: col,
    accessorFn: (row) =>
        dayjs(row[col]).isValid()
            ? dayjs(row[col]).format(dateFormat)
            : dayjs('1/1/1002').format('MM/DD/YYYY'),
    cell: (info) => (
        <div>
            {dayjs(info.getValue()).format(dateFormat).includes('01/01/1002')
                ? ''
                : dayjs(info.getValue()).format(dateFormat)}
        </div>
    ),
    filterFn: dateFilterFn,
    sortingFn: (a, b) =>
        dayjs(a.getValue(col)).toDate() - dayjs(b.getValue(col)).toDate(),
})

const sqlServerSort = (rowA, rowB, columnId, desc) => {
    const a = rowA.getValue(columnId)
    const b = rowB.getValue(columnId)

    // Handle undefined/null/empty - SQL Server puts NULLs at the end in ASC order
    if (a === undefined || a === null || a === '') return desc ? -1 : 1
    if (b === undefined || b === null || b === '') return desc ? 1 : -1

    // Convert to strings for comparison
    const aStr = String(a)
    const bStr = String(b)

    // SQL Server's default string comparison is lexicographic
    return aStr.localeCompare(bStr, 'en-US', {
        numeric: false,
        sensitivity: 'base',
        caseFirst: 'upper',
    })
}

const createStationCodeField = (col) => ({
    header: () => <div>{col}</div>,
    id: col,
    accessorFn: (row) =>
        row[col] === null || row[col] === undefined || row[col] === ''
            ? undefined
            : row[col],
    cell: (info) => <div>{info.getValue() ?? ''}</div>,
    // sortUndefined: 'last',
    sortingFn: sqlServerSort,
})

const parseColumns = (data, dateColumns) => {
    if (!(data && data.length)) {
        return []
    }

    const exampleRow = data[0]
    const columns = Object.keys(exampleRow).map((col) => {
        const isDate = dateColumns.indexOf(col) !== -1
        if (isDate) return createDateField(col)
        if (col === 'Station Code') return createStationCodeField(col)
        return {
            header: () => <div>{col}</div>,
            accessorKey: col,
            cell: (info) => <div>{info.getValue()}</div>,
        }
    })
    return columns
}

const parseColumnsWithConfig = (data, config) => {
    if (data && data.length && config && config.length) {
        const exampleRow = data[0]
        const columns = Object.keys(exampleRow).map((col) => {
            let columnName = col
            const configObj = config.find((x) => x.ColumnName === col)
            if (configObj) {
                columnName = configObj.ColumnTitle
            }
            return {
                header: () => <div>{columnName}</div>,
                accessorKey: col,
                cell: (info) => {
                    let value = info.getValue()
                    if (configObj) {
                        if (configObj.Values) {
                            let configValue = configObj.Values.find((x) =>
                                x.code != null && value != null
                                    ? x.code === value ||
                                      x.code.toString() === value.toString()
                                    : null
                            )
                            value =
                                configValue && configValue.codedescription
                                    ? configValue.codedescription
                                    : null
                        }
                    }
                    return <div>{value}</div>
                },
            }
        })
        return columns
    } else {
        return []
    }
}

const getInProgressColumns = (data, setConfirmDeleteModalData) => {
    const columns = parseColumns(data, ['Modify Date']).filter(
        (x) =>
            x.accessorKey !== 'Link' &&
            x.accessorKey !== 'IsValid' &&
            x.id !== 'Link' &&
            x.id !== 'IsValid'
    )

    return [
        {
            header: () => <div>Validation Status</div>,
            accessorKey: 'IsValid',
            cell: (info) => {
                const isValid = info.row.getValue('IsValid') === 'Pass'
                return (
                    <div>
                        {isValid ? (
                            <span className="icon-text">
                                <span className="icon">
                                    <i className="icon">
                                        <FaCheck className="has-text-success" />
                                    </i>{' '}
                                </span>
                                <span>Pass </span>
                            </span>
                        ) : (
                            <span className="icon-text">
                                <span className="icon">
                                    <i className="icon">
                                        <FaTimes className="has-text-danger" />
                                    </i>{' '}
                                </span>
                                <span>Fail </span>
                            </span>
                        )}
                    </div>
                )
            },
        },
        ...columns,
        {
            header: () => <div>Actions</div>,
            accessorKey: 'nothing',
            enableSorting: false,
            enableColumnFilter: false,
            cell: (info) => {
                const recordValues = info.row.original
                return (
                    <div>
                        <Link
                            to={{
                                pathname: `submissions/${recordValues.Link}/form/${recordValues['Form ID']}`,
                                // pathname: recordValues['Is EDD?'] === 'Yes'
                                //     ? `submissions/${recordValues.Link}/edd/${recordValues['Form ID']}`
                                //     : `submissions/${recordValues.Link}/form/${recordValues['Form ID']}`,
                                state: {
                                    from: 'inProgress',
                                    stationCode: recordValues['Station Code'],
                                },
                            }}
                        >
                            Edit
                        </Link>
                        {` | `}
                        <div
                            className="link inline-block"
                            onClick={() =>
                                setConfirmDeleteModalData({
                                    display: true,
                                    uploadId: recordValues['Form ID'],
                                    formType: recordValues['Form Type'],
                                    submittingEDD:
                                        recordValues['Submitting EDD'],
                                })
                            }
                        >
                            Delete
                        </div>
                    </div>
                )
            },
        },
    ]
}

const getSubmittedColumns = (data, isReviewer, activeTab) => {
    const columns = parseColumns(data, [
        'Submitted Date',
        'Approved / Denied Date',
    ]).filter(
        (x) =>
            x.accessorKey !== 'Link' &&
            x.accessorKey !== 'IsValid' &&
            x.id !== 'Link' &&
            x.id !== 'IsValid'
    )
    return [
        ...(activeTab === 'submitted'
            ? [
                  {
                      header: () => <div>Validation Status</div>,
                      accessorKey: 'IsValid',
                      cell: (info) => {
                          const isValid =
                              info.row.getValue('IsValid') === 'Pass'
                          return (
                              <div>
                                  {isValid ? (
                                      <span className="icon-text">
                                          <span className="icon">
                                              <i className="icon">
                                                  <FaCheck className="has-text-success" />
                                              </i>{' '}
                                          </span>
                                          <span>Pass </span>
                                      </span>
                                  ) : (
                                      <span className="icon-text">
                                          <span className="icon">
                                              <i className="icon">
                                                  <FaTimes className="has-text-danger" />
                                              </i>{' '}
                                          </span>
                                          <span>Fail </span>
                                      </span>
                                  )}
                              </div>
                          )
                      },
                  },
              ]
            : []),
        ...columns,
        {
            header: () => <div>Actions</div>,
            accessorKey: 'none',
            enableSorting: false,
            enableColumnFilter: false,
            cell: (info) => {
                const recordValues = info.row.original
                return (
                    <div>
                        {recordValues['Form Type'] !==
                            'Depth Profile Sample Result' && (
                            <Link
                                to={{
                                    pathname: `submissions/${recordValues.Link}/form/view/${recordValues['Form ID']}`,
                                    state: { from: activeTab },
                                    stationCode: recordValues['Station Code'],
                                }}
                            >
                                View
                            </Link>
                        )}
                        {isReviewer && activeTab === 'submitted' && (
                            <>
                                {` | `}
                                <Link
                                    to={{
                                        pathname: `submissions/${recordValues.Link}/form/review/${recordValues['Form ID']}`,
                                        state: {
                                            from: activeTab,
                                            stationCode:
                                                recordValues['Station Code'],
                                        },
                                    }}
                                >
                                    Review
                                </Link>
                            </>
                        )}
                    </div>
                )
            },
        },
    ]
}

const getEmptyColumnObject = (columns) => {
    return columns.reduce((acc, curr) => {
        const field = curr.accessorKey || curr.id
        return {
            ...acc,
            [field]: null,
        }
    }, {})
}

export {
    getInProgressColumns,
    getSubmittedColumns,
    parseColumns,
    getEmptyColumnObject,
    parseColumnsWithConfig,
}
